import {
  QueryBuilderClass,
  QueryBuilderProperty,
} from 'bones/components/molecules/QueryBuilder/types/model';
import { RDF_TYPE_IRI } from 'bones/constants/iris';
import {
  DatatypeOperatorTypeValue,
  DatatypeRange,
  Direction,
  DirectionValue,
  OperatorType,
  OperatorTypeValue,
  TEXT_BY_OPERATOR_TYPE,
  XSD_DATATYPE_IRIS,
} from 'bones/utils/queryBuilder/constants';
import { SelectItem } from 'bones/utils/select/types';

export interface ClassSubSelectItem extends SelectItem {
  items?: never;
  iri: string;
}
export interface ClassSelectItem extends SelectItem<ClassSubSelectItem> {
  iri?: never;
  colorId: string;
}

export const createClassSelectItem = ({
  iri,
  label,
  colorId,
  className,
}: Pick<QueryBuilderClass, 'iri' | 'label' | 'colorId'> & {
  className?: string;
}): ClassSelectItem => ({
  text: label,
  value: iri,
  collapseSingleItem: true,
  colorId,
  items: [],
  className,
});

export interface PropertySubSelectItem extends SelectItem {
  iri: string;
  label: string;
  direction: DirectionValue;
  operatorType: OperatorTypeValue;
  range: DatatypeRange;
}

export type PropertySelectItem =
  | PropertySubSelectItem
  | SelectItem<PropertySubSelectItem>;

const PROPERTY_VALUE_SPLIT_CHAR = '^';

export const getPropertyValue = (iri: string, direction: DirectionValue) =>
  `${iri}${PROPERTY_VALUE_SPLIT_CHAR}${direction}`;

export const parsePropertyValue = (
  value: string
): { iri: string; direction: DirectionValue } => {
  const [iri, direction] = value.split(PROPERTY_VALUE_SPLIT_CHAR);
  return { iri, direction: direction as any };
};

export const createPropertySubSelectItem = (
  {
    iri,
    label,
    operatorType,
    ranges,
  }: Pick<QueryBuilderProperty, 'iri' | 'label' | 'ranges'> & {
    operatorType: OperatorTypeValue;
  },
  direction: DirectionValue
): PropertySubSelectItem => ({
  text:
    operatorType !== OperatorType.OBJECT || iri === RDF_TYPE_IRI
      ? label
      : `${label} ${direction === Direction.OUTBOUND ? '→' : '←'}`,
  value: getPropertyValue(iri, direction),
  iri,
  label,
  direction,
  operatorType,
  range:
    (operatorType === OperatorType.DATATYPE &&
      Array.from(ranges).find((rangeIri) =>
        XSD_DATATYPE_IRIS.includes(rangeIri)
      )) ||
    null,
});

export interface ComparisonOperatorTypeSelectItem extends SelectItem {
  comparison: true;
  comparisonValue: string;
}

export interface OperatorTypeSelectItem
  extends SelectItem<ComparisonOperatorTypeSelectItem> {
  comparison: false;
}

export const createOperatorTypeSelectItem = (
  operatorType: DatatypeOperatorTypeValue
): OperatorTypeSelectItem => ({
  text: TEXT_BY_OPERATOR_TYPE[operatorType],
  value: operatorType,
  comparison: false,
});

export const createComparisonOperatorTypeSelectItem = (
  operatorType: DatatypeOperatorTypeValue
): ComparisonOperatorTypeSelectItem => ({
  text: TEXT_BY_OPERATOR_TYPE[operatorType],
  value: `comparison-${operatorType}`,
  comparisonValue: operatorType,
  comparison: true,
});
